import Icon from 'antd/lib/icon';
import Tooltip from 'antd/lib/tooltip';
import moment from 'moment';
import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'src/Library/Types';
import SEFieldInput from '../../../SECommon/SEFieldInput';
import useDocument from 'src/hooks/form/useDocument';
import uuid from 'uuid';
import FileViewer from 'src/components/common/FileViewer';
import { useGenericState } from 'src/Library/UseGenericState';

interface SEDocumentBarProps extends StylesProps<ReturnType<typeof styles>> {
  document: {
    updatedAt: moment.Moment | string;
    url: string;
    documentType: string;
    name: string;
    fileName: string;
    documentId: string;
  },
  index?: number;
  showTitle: boolean;
  onRemove?: () => void;
  isDeleteDisabled?: boolean;
  showColumnHeader?: boolean;
  containerBoxStyle?: Record<string, string>;
  form: any;
  editedDocumentList: any;
  setEditedDocumentList: (newDoc: any) => void;
  setIsUpdateDisabled: (isUplaoding: boolean) => void;
  editable: boolean;
  isParentTask: boolean;
}

interface StateProps {
  fileToPreview?: { url: string, title: string };
  isFileDrawerVisible: boolean;
}

const styles = theme => ({
  contentTitle: {
    color: theme.colors.text,
    fontFamily: 'Open Sans',
    fontSize: theme.fontSize.h3,
    fontWeight: 'bold',
    letterSpacing: 0,
  },
  line: {
    borderBottom: '1px solid #D9D9D9',
    opacity: 0.5,
    height: 1,
    width: '98%',
    marginTop: 6,
    marginBottom: 20,
  },
  spaceBetween: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    color: '#666666',
    lineHeight: '14px',
    marginLeft: theme.spacing.ms,
    fontSize: theme.fontSize.small,
  },
  value: {
    color: theme.colors.text,
    marginLeft: theme.spacing.ms,
    fontSize: theme.fontSize.small,
    lineHeight: '14px',
    fontWeight: theme.fontWeight.semibold,
  },
  contentBox: {
    margin: `20px ${theme.spacing.ms}px`,
  },
  addDocument: {
    marginLeft: theme.spacing.sm,
    borderRadius: 13,
    cursor: 'pointer',
    backgroundColor: '#FAFAFA',
    fontWeight: 'bold',
    fontSize: theme.fontSize.small,
    color: theme.colors.primaryDark,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
});

const SEDocumentBar: React.FunctionComponent<SEDocumentBarProps> = ({
  classes,
  document,
  onRemove,
  index = 0,
  showTitle = true,
  containerBoxStyle,
  isDeleteDisabled = true,
  showColumnHeader = true,
  form,
  editedDocumentList,
  setEditedDocumentList,
  setIsUpdateDisabled,
  editable,
  isParentTask,
}) => {
  const { updatedAt, url, documentType, name, fileName, documentId } = document;
  const [state, setState] = useGenericState<StateProps>({
    fileToPreview: undefined,
    isFileDrawerVisible: false,
  });

  const documentInput = useDocument({
    form,
    disabled: false,
    visible: true,
    label: '',
    multi: true,
    isMulti: true,
    accept: '.pdf, .jpg, .jpeg, .png, .xlsx, .xls, .csv, .doc, .docx, .msg, .eml',
  });

  React.useEffect(() => {
    if (!documentInput.isUploading && documentInput.s3File.s3Link) {
      const docListMap = {...editedDocumentList};
      const newDoc = {
        name: documentInput.s3File.name,
        s3Link: documentInput.s3File.s3Link,
        uid: uuid()
      };
      if (docListMap[documentId]) {
        docListMap[documentId].push(newDoc);
      } else {
        docListMap[documentId] = [newDoc];
      }
      setEditedDocumentList(docListMap);
    }
  }, [documentInput.isUploading]);

  React.useEffect(() => {
    if (document?.documentId) {
      setIsUpdateDisabled(documentInput.isUploading)
    }
  }, [documentInput.isUploading])

  const renderContentTitle = (title: string) => {
    if (!showTitle) {
      return;
    }
    return (
      <div className={classes.spaceBetween}>
        <div className={classes.contentTitle}>{title}</div>
        {(editable && !isParentTask) ? <div>{documentInput.decorator(<SEFieldInput field={documentInput} />)}</div> : null}
      </div>
    );
  };

  const renderLine = () => {
    if (!showTitle) {
      return;
    }
    return <div className={classes.line}/>;
  };

  const renderAttachmentColumns = () => {
    if (!showColumnHeader) {
      return;
    }

    return <div className={classes.spaceBetween} style={{ marginBottom: 16 }}>
      <div className={classes.title} style={{ width: '5%' }}>S No.</div>
      <div className={classes.title} style={{ width: '39%' }}>File Name</div>
      <div className={classes.title} style={{ width: '20%' }}>Document Type</div>
      <div className={classes.title} style={{ width: '20%' }}>Uploaded at</div>
      <div style={{ width: 21 }}></div>
      <div style={{ width: 21 }}></div>
    </div>;
  };

  const renderDelete = () => {
    if (!onRemove || typeof onRemove !== 'function') {
      return <div style={{ width: 21 }} ></div>;
    }

    return (
      <Icon
        style={{
          color: isDeleteDisabled ? 'lightgrey' : '#DF1D1D',
          cursor: isDeleteDisabled ? 'not-allowed' : 'pointer',
          marginLeft: 8,
        }}
        type="delete"
        onClick={() => {
          if (isDeleteDisabled) {
            return;
          }
          onRemove();
        }}
      />
    );
  }

  const renderFilePreviewDrawer = () => {
    return (
      <FileViewer
        url={state.fileToPreview?.url}
        title={state.fileToPreview?.title}
        onClose={() => {
          setState({
            fileToPreview: undefined,
            isFileDrawerVisible: false,
          });
        }}
      />
    );
  }

  const previewFile = (url: string, title: string) => {
    if (!url) {
      return;
    }
    setState({
      isFileDrawerVisible: true,
      fileToPreview: { url, title },
    });
  }
  const handleDownload = async (url: string, fileName: string) => {
    try {
      // Fetch the file content
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const tempUrl = URL.createObjectURL(blob);
      
      // Create a temporary <a> element to trigger the download
      const link = window.document.createElement('a');  // Correct usage
      link.href = tempUrl;
      link.download = fileName || name;  // Specify the file name here
      window.document.body.appendChild(link);   // Append the link to the document
      link.click();                      // Programmatically click the link to start the download
      window.document.body.removeChild(link);   // Clean up by removing the link
      URL.revokeObjectURL(tempUrl);      // Clean up the object URL
    } catch (error) {
      console.error('Download failed:', error);
    }
  };
  const renderAttachmentValues = ({ updatedAt, url, documentType, name, fileName }) => {
    return (
      <div
        className={classes.spaceBetween}
        style={{
          marginBottom: 10,
          backgroundColor: '#FCFAF4',
          padding: 8,
        }}>
        <div className={classes.value} style={{ width: '5%' }}>{index + 1}</div>
        <Tooltip title={fileName || name}>
          <div className={classes.addDocument} style={{ width: '40%', backgroundColor: 'inherit' }}>
            <a onClick={() => previewFile(url, fileName || name)}>{fileName || name}</a>
          </div>
        </Tooltip>
        <div className={classes.value} style={{ width: '20%' }}>{documentType}</div>
        <div className={classes.value} style={{ width: '20%' }}>
          {moment(updatedAt).format('DD MMM YYYY | hh:mm A')}
        </div>
        <a onClick={() => handleDownload(url, fileName || name)}>
          <Icon type="download" style={{ fontSize: 16, color: '#333333' }} />
        </a>
        {renderDelete()}
      </div>
    );
  };
  

  return ( <div className={classes.contentBox} style={containerBoxStyle}>
    {renderContentTitle(documentType)}
    {renderLine()}
    {renderAttachmentColumns()}
    {renderAttachmentValues({ updatedAt, url, documentType, name, fileName })}
    {state.isFileDrawerVisible && renderFilePreviewDrawer()}
  </div>
  );
}

export default withStyles(styles)(SEDocumentBar);
